import * as React from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shigo-ui/ui';

import { dashboardT, type DashboardMessageKey } from '../../../../../../lib/messages';
import { PlacementApprovalStatus, type PlacementDemandIntegration } from '../../../model';

const approvalStatusLabelKeys: Record<PlacementApprovalStatus, DashboardMessageKey> = {
  [PlacementApprovalStatus.Pending]: 'placements.approvalStatuses.pending',
  [PlacementApprovalStatus.Approved]: 'placements.approvalStatuses.approved',
  [PlacementApprovalStatus.Rejected]: 'placements.approvalStatuses.rejected',
  [PlacementApprovalStatus.Submitted]: 'placements.approvalStatuses.submitted',
  [PlacementApprovalStatus.NotApplicable]: 'placements.approvalStatuses.notApplicable',
};

const editableIntegrationFields = [
  'publisherId',
  'siteId',
  'app_id',
  'zone_id',
  'tag_id',
  'display_manager',
  'triplelift_inventory_code',
  'openx_del_domain',
  'unruly_targeting_uuid',
  'openx_unit_id',
  'smart_ad_server_page_id',
  'smart_ad_server_network_id',
  'smart_ad_server_format_id',
] as const;

export function PlacementIntegrationEditDialog(props: {
  integration: PlacementDemandIntegration;
  onOpenChange: (open: boolean) => void;
  onSave: (value: PlacementDemandIntegration) => void;
}) {
  // An editable copy that is applied on save; the dialog mounts per edited integration.
  // react-doctor-disable-next-line react-doctor/no-derived-useState
  const [draft, setDraft] = React.useState(props.integration);
  return (
    <Dialog open onOpenChange={props.onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-[760px] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{dashboardT('placements.editIntegration')}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 md:grid-cols-2">
          <Select
            value={draft.approvalStatus ?? PlacementApprovalStatus.Pending}
            onValueChange={(approvalStatus) => {
              setDraft({ ...draft, approvalStatus: approvalStatus as PlacementApprovalStatus });
            }}
          >
            <SelectTrigger aria-label={dashboardT('placements.approvalStatus')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.values(PlacementApprovalStatus).map((status) => (
                <SelectItem key={status} value={status}>
                  {dashboardT(approvalStatusLabelKeys[status])}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            aria-label={dashboardT('placements.integrationField.placementId')}
            placeholder={dashboardT('placements.integrationField.placementId')}
            value={draft.placementId ?? ''}
            readOnly
          />
          {editableIntegrationFields.map((field) => (
            <Input
              key={field}
              aria-label={dashboardT(`placements.integrationField.${field}`)}
              placeholder={dashboardT(`placements.integrationField.${field}`)}
              value={draft[field] ?? ''}
              onChange={(event) => {
                setDraft({ ...draft, [field]: event.target.value });
              }}
            />
          ))}
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              props.onOpenChange(false);
            }}
          >
            {dashboardT('placements.cancel')}
          </Button>
          <Button
            type="button"
            onClick={() => {
              props.onSave(draft);
            }}
          >
            {dashboardT('placements.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
